import React, { useEffect } from "react";
import Home from "./pages/Home";
import Layout from "./components/Layout";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router";
import CTLayout from "./components/CTLayout";
import CTProductPreview from "./pages/CTProductPreview";
import CTHome from "./pages/CTHome";
import CTProductCostSetup from "./pages/CTProductCostSetup";
import { AppDispatch, RootState } from "./store";
import { useDispatch, useSelector } from "react-redux";
import {
  getUserProfile,
  getUserAvatar,
  getFrontendPermissions,
  resetUseProfile,
} from "./store/slices/UserProfileSlice";
import "./App.scss";
import ProductDetail from "./pages/ProductDetail";
import SessionPopUp from "./components/SessionPopUp";
import Login from "./pages/Login";
import { resetUseSessionMessage } from "./store/slices/UserSessionMessageSlice";
import { LoginValidation } from "./LoginValidation";
import useSessionPopup from "./hooks/useSessionPopup";
import Checkout from "./pages/Checkout";
import {
  getAccessTokenFromLocal,
  markExplicitLogout,
  removeAccessTokenFromLocal,
  removeOpportunityCodeFromLocal,
} from "./utils/common";
import {
  AUTH_SYNC_LOCAL_STORAGE_KEY,
  SESSION_CONTINUE_LOCAL_STORAGE_KEY,
  SESSION_LOGOUT_COMPLETE_LOCAL_STORAGE_KEY,
  SESSION_LOGOUT_LOCAL_STORAGE_KEY,
} from "./Constant";
import BMOHome from "./pages/BMOHome";
import BMOSkuApproval from "./pages/BMOSkuApproval";
import ProtectedRoute from "./ProtectedRoute";
import Consumption from "./pages/Consumption";
import CTMarketplace from "./pages/CTMarketplace";
import MyWorkspacePage from "./pages/MyWorkspacePage";
import OrderPipelinePage from "./pages/OrderPipelinePage";
import Report from "./pages/Report";
import ReportDetail from "./pages/ReportDetail";
import HttpError from "./components/HttpError/HttpError";
import NotFound from "./components/HttpError/NotFound";
import { setNavigator } from "./utils/navigation";
import useRichTextEditorBlurOnScroll from "./hooks/useRichTextEditorBlurOnScroll";
import OrderExtension from "./pages/OrderExtension/OrderExtension";
import { getAppConfigurations } from "./store/slices/AppConfigurationSlice";
import AdminSupport from "./pages/AdminSupport";
import {
  logoutFromAnotherTab,
  logoutStartedFromAnotherTab,
  resetUserLogout,
  resetUserLoginError,
} from "./store/slices/UserLogoutSlice";
import Loader from "./components/Loader";
import EngagementDelegation from "./components/MyWorkspace/EngagementDelegation";
// import AdminEngagement from "./pages/AdminEngagement";
import AdminCTBM from "./pages/AdminCTBM";

declare global {
  interface Window {
    SN_CSM_EC?: {
      init: (config: {
        moduleID: string;
        loadFeature: unknown;
        hideEMByDefault?: boolean;
      }) => void;
      loadEMFeature: () => unknown;
      show: (show: boolean) => void;
      onLogin: () => void;
      onLogout: () => void;
      hide: () => void;
      close: () => void;
      open: () => void;
    };
  }
}

const App: React.FC = () => {
  useSessionPopup(); // This will handle the session timeout and popup logic
  const accessTken = getAccessTokenFromLocal();
  const [isAuthReady, setAuthReady] = React.useState<boolean>(false);
  const [isLoginInprogress, setIsLoginInprogress] =
    React.useState<boolean>(false);
  const dispatch = useDispatch<AppDispatch>();
  const showSessionMessage = useSelector(
    (state: RootState) => state.UserSessionMessageSlice.display,
  );

  const isLogout = useSelector(
    (state: RootState) => state.UserLogoutSlice.isLogout,
  );

  const isLogoutProgress = useSelector(
    (state: RootState) => state.UserLogoutSlice.isLogoutProgress,
  );

  const userPermissions: string[] = useSelector(
    (state: RootState) => state.UserProfileSlice.userPermissions,
  );

  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (import.meta.env.VITE_ENABLE_SN === "true") {
      const scriptUrl = import.meta.env.VITE_SN_SCRIPT_URL;
      const moduleId = import.meta.env.VITE_SN_MODULE_ID;

      if (!scriptUrl || !moduleId) {
        return;
      }

      const initializeEngagementMessenger = () => {
        if (!window.SN_CSM_EC) {
          return;
        }

        window.SN_CSM_EC.init({
          moduleID: moduleId,
          loadFeature: window.SN_CSM_EC.loadEMFeature(),
          hideEMByDefault: false,
        });

        window.SN_CSM_EC.onLogin();
      };

      const existingScript = document.getElementById(
        "sn-csm-ec-script",
      ) as HTMLScriptElement | null;

      if (existingScript) {
        initializeEngagementMessenger();
        existingScript.addEventListener("load", initializeEngagementMessenger);

        return () => {
          existingScript.removeEventListener(
            "load",
            initializeEngagementMessenger,
          );
        };
      }

      const script = document.createElement("script");
      script.id = "sn-csm-ec-script";
      script.src = scriptUrl;
      script.async = true;
      script.addEventListener("load", initializeEngagementMessenger);
      document.head.appendChild(script);

      return () => {
        script.removeEventListener("load", initializeEngagementMessenger);
      };
    }
  }, []);

  useRichTextEditorBlurOnScroll(); //This handles page scrolling when focus is within a rich-text editor.
  const navigate = useNavigate();

  useEffect(() => {
    setNavigator((path: string, opts?: { replace?: boolean; state?: any }) =>
      navigate(path, opts),
    );

    window.SN_CSM_EC?.close();
  }, [navigate]);

  useEffect(() => {
    if (isLogout && !isLogoutProgress) {
      sessionStorage.setItem("redirectTo", "/");
      navigate("/", { replace: true });
    }
  }, [isLogout, isLogoutProgress, navigate]);

  useEffect(() => {
    if (hash == "") {
      window.scrollTo(0, 0);
      sessionStorage.setItem(
        "redirectTo",
        window.location.pathname + window.location.search,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    if (isAuthReady) {
      dispatch(getUserProfile());
      dispatch(getUserAvatar());
      dispatch(getFrontendPermissions());
      dispatch(getAppConfigurations());
    }
  }, [dispatch, isAuthReady]);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      // The storage event is raised in every other tab/window, not the one
      // that changed localStorage.
      if (
        event.key === AUTH_SYNC_LOCAL_STORAGE_KEY &&
        event.newValue !== null
      ) {
        // Re-run the existing-token login flow in this tab.
        setAuthReady(false);
        dispatch(resetUserLogout());
        return;
      }

      if (
        event.key === SESSION_CONTINUE_LOCAL_STORAGE_KEY &&
        event.newValue !== null
      ) {
        dispatch(resetUseSessionMessage());
        return;
      }

      if (
        event.key === SESSION_LOGOUT_LOCAL_STORAGE_KEY &&
        event.newValue !== null
      ) {
        dispatch(logoutStartedFromAnotherTab());
        dispatch(resetUseSessionMessage());
        return;
      }

      if (
        event.key === SESSION_LOGOUT_COMPLETE_LOCAL_STORAGE_KEY &&
        event.newValue !== null
      ) {
        if (event.newValue.endsWith(":success")) {
          dispatch(logoutFromAnotherTab());
          dispatch(resetUseProfile());
          dispatch(resetUseSessionMessage());
          markExplicitLogout();
          removeAccessTokenFromLocal();
          removeOpportunityCodeFromLocal();
        } else {
          dispatch(resetUserLogout());
        }
        return;
      }

      if (event.key !== "accessToken" || event.newValue !== null) {
        return;
      }

      dispatch(logoutFromAnotherTab());
      dispatch(resetUseProfile());
      dispatch(resetUseSessionMessage());
      markExplicitLogout();
      removeOpportunityCodeFromLocal();
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [dispatch]);

  useEffect(() => {
    if (isLogout || !accessTken || accessTken === "" || accessTken === null) {
      // Clear user data or perform logout actions here
      dispatch(resetUseProfile());
      dispatch(resetUseSessionMessage());
    }
  }, [accessTken, dispatch, isLogout]);

  window.addEventListener("resetTokenMismatch", () => {
    if (window.localStorage.getItem("isTokenMismatch") === "true") {
      dispatch(resetUserLoginError());
      dispatch(resetUseSessionMessage());
      removeOpportunityCodeFromLocal();
      window.localStorage.setItem("isTokenMismatch", "false");
      return;
    }
  });

  const updateTheme = (newTheme: "light" | "dark") => {
    document.body.classList.remove("motif-theme-light", "motif-theme-dark");
    document.body.classList.add(`motif-theme-${newTheme}`);
    document.body.setAttribute("motif-theme", newTheme);
  };

  if (
    isLogoutProgress ||
    isLoginInprogress ||
    window.localStorage.getItem("isTokenMismatch") === "true"
  ) {
    return (
      <div className="initial-loader">
        <Loader />
      </div>
    );
  }

  if (isLogout && !isLogoutProgress) {
    return (
      <Login
        setVerifyLogin={setAuthReady}
        loginInProgress={isLoginInprogress}
      />
    );
  }

  const isPermissionsLoaded = userPermissions.length > 0;
  const isBMOUser =
    isPermissionsLoaded && userPermissions.includes("system_bmo");

  const isAllowed = isPermissionsLoaded ? isBMOUser : true;
  return (
    <>
      {isAuthReady ? (
        <Routes>
          <Route path="/" element={<Layout updateTheme={updateTheme} />}>
            <Route index element={<Home />} />
            <Route path="/marketplace" element={<CTMarketplace />} />
            <Route path="product/:id" element={<ProductDetail />} />
            <Route path="/checkout/order?/:orderID?" element={<Checkout />} />
            <Route path="/consumption" element={<Consumption />} />
            <Route path="/report" element={<Report />} />
            <Route path="/report/:id" element={<ReportDetail />} />
            <Route
              path="my-workspace/engagement-delegation"
              element={<EngagementDelegation />}
            />
            <Route
              path="/admin"
              element={<Navigate to="/admin/support" replace />}
            />
            <Route path="/admin/support" element={<AdminSupport />} />
            {/* <Route
              path="/admin/engagement-override"
              element={<AdminEngagement />}
            />*/}
            <Route path="/admin/ct-bm" element={<AdminCTBM />} />
            <Route path="my-workspace/:tab?" element={<MyWorkspacePage />} />
            <Route path="/error" element={<HttpError />} />
            <Route
              path="order-pipeline/:tab?"
              element={<OrderPipelinePage />}
            />
            <Route
              path="/order-extension/:orderID/sku-id?/:skuID?"
              element={<OrderExtension />}
            />

            <Route path="/*" element={<NotFound />} />
          </Route>

          <Route path="/ct" element={<CTLayout updateTheme={updateTheme} />}>
            <Route index element={<CTHome />} />
            <Route path="product/:id/preview" element={<CTProductPreview />} />
            <Route
              path="/ct/product/:id/product-cost-setup"
              element={<CTProductCostSetup />}
            />
            <Route
              path="bmo"
              element={
                <ProtectedRoute isAllowed={isAllowed} redirectTo="/ct">
                  <BMOHome />
                </ProtectedRoute>
              }
            />
            <Route
              path="bmo/product/:id/sku-id/:skuID/sku-approval"
              element={
                <ProtectedRoute isAllowed={isAllowed} redirectTo="/ct">
                  <BMOSkuApproval />
                </ProtectedRoute>
              }
            />
          </Route>
        </Routes>
      ) : (
        <LoginValidation
          setAuthReady={setAuthReady}
          loginInProgress={isLoginInprogress}
          setIsLoginInprogress={setIsLoginInprogress}
        />
      )}

      {showSessionMessage && <SessionPopUp isVisible={showSessionMessage} />}
    </>
  );
};

export default App;
